'use client'

import { ReactNode, useState } from 'react'
import { Text, Cell, Icon, Card } from '@vinted/web-ui'
import { ExclamationTriangleFilled24 } from '@vinted/monochrome-icons'

import { CurrencyAmountModel } from '@marketplace-web/currency/currency-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import { clickEvent } from '_libs/common/event-tracker/events'

import { ElectronicsVerificationModal } from '@marketplace-web/escrow-pricing/escrow-pricing-feature'

type Props = {
  electronicsVerificationFee?: CurrencyAmountModel | null
}

const ElectronicsVerificationBanner = ({ electronicsVerificationFee }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const translate = useTranslate('bundle_items')
  const { track } = useTracking()

  const handleElectronicsVerificationClick = () => {
    setIsModalOpen(true)

    track(
      clickEvent({
        screen: Screen.Bundling,
        target: ClickableElement.ElectronicsVerificationBuyer,
      }),
    )
  }

  if (!electronicsVerificationFee) return null

  const values = {
    'how-it-works': (chunks: Array<ReactNode>) => (
      <span key="how-it-works">
        <button type="button" onClick={handleElectronicsVerificationClick}>
          <Text
            clickable
            text={chunks}
            type={Text.Type.Subtitle}
            testId="electronics-verification-info-banner-link"
            as="span"
          />
        </button>
      </span>
    ),
  }

  return (
    <>
      <Card
        experimentalTheme="exposeLightExperimental"
        testId="electronics-verification-info-banner"
      >
        <Cell
          prefix={<Icon name={ExclamationTriangleFilled24} color={Icon.Color.Expose} />}
          body={
            <Text
              as="h3"
              text={translate('electronics_verification_note', values)}
              type={Text.Type.Subtitle}
              theme="amplified"
            />
          }
          theme="transparent"
        />
      </Card>
      <ElectronicsVerificationModal
        screen={Screen.Bundling}
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        verificationFee={electronicsVerificationFee}
        isViewingOwnItem={false}
      />
    </>
  )
}

export default ElectronicsVerificationBanner
